import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip } from "@/components/StatusChip";
import { PersonAvatar } from "@/components/PersonAvatar";
import { UserPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ROLE_TONE = { Owner: "blue", Admin: "violet", Member: "neutral", Viewer: "neutral" };

export const TeamSection = () => {
  const [members, setMembers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Member");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/workspace/members").then((r) => setMembers(r.data)).catch(() => toast.error("Could not load team"));
  }, []);

  const add = async () => {
    if (!name.trim() || !email.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/workspace/members", { name: name.trim(), email: email.trim(), role });
      setMembers((m) => [...m, r.data]);
      setName("");
      setEmail("");
      setRole("Member");
      toast.success(`${r.data.name} added to the workspace`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add teammate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-team-section">
      <div className="space-y-1" data-testid="team-member-list">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-2.5 rounded-xl border border-[#1e1e1e] bg-[#141414] px-3 py-2" data-testid="team-member-row">
            <PersonAvatar name={m.name} size={26} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-medium text-white">{m.name}</div>
              <div className="truncate text-[10.5px] text-[#6b6b6b]">{m.email}</div>
            </div>
            <Chip tone={ROLE_TONE[m.role] || "neutral"}>{m.role}</Chip>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-[#1e1e1e] bg-[#0d0d0d] p-3">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Add a teammate</div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="team-add-name"
              className="h-8 border-[#2a2a2a] bg-[#161616] text-[12.5px] text-white placeholder:text-[#555]" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 w-32 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="team-add-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#222] bg-[#161616] text-white">
                {["Admin", "Member", "Viewer"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="work email" data-testid="team-add-email"
              onKeyDown={(e) => e.key === "Enter" && add()}
              className="h-8 border-[#2a2a2a] bg-[#161616] text-[12.5px] text-white placeholder:text-[#555]" />
            <Button onClick={add} disabled={!name.trim() || !email.trim() || busy} data-testid="team-add-submit"
              className="h-8 btn-gradient px-3 text-[12px] font-medium text-white disabled:opacity-40">
              {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <UserPlus size={13} className="mr-1" />} Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
